import React, { useState, useEffect } from 'react';
import {
  Layout,
  Row,
  Col,
  Space,
  Typography,
  Button,
  Alert,
  Card,
  Table
} from 'antd';
import {
  ReloadOutlined,
  DashboardOutlined,
  TeamOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';

// Import services
import dashboardService from '../../../services/dashboardService';
import { getInvoiceStatistics } from '../../../services/invoiceService';

// Import components
import StatCard from '../../../components/dashboard/StatCard';
import OrderStatusChart from '../../../components/dashboard/OrderStatusChart';
import FinancialSummary from '../../../components/dashboard/FinancialSummary';
import RecentActivity from '../../../components/dashboard/RecentActivity';
import { DashboardSkeleton } from '../../../components/common/SkeletonLoader';

const { Content } = Layout;
const { Title, Text } = Typography;

const AdminDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [upcomingDeliveries, setUpcomingDeliveries] = useState([]);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Dashboard verilerini getir
  const fetchDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const [data, products, deliveries, invoiceStatsRes] = await Promise.all([
        dashboardService.getFullStatistics(),
        dashboardService.getTopProductsThisMonth(),
        dashboardService.getUpcomingDeliveries(7),
        getInvoiceStatistics().then(res => res.data).catch(() => null)
      ]);
      setDashboardData(data);
      setTopProducts(products || []);
      setUpcomingDeliveries(deliveries || []);
      setInvoiceStats(invoiceStatsRes);
      setLastUpdate(new Date());

      if (isRefresh) {
        toast.success('Dashboard basariyla yenilendi!', {
          icon: '🔄'
        });
      }
    } catch (err) {
      console.error('Dashboard getirme hatasi:', err);
      const errorMessage = err.message || 'Dashboard verileri yuklenemedi';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Ilk yukleme
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Her 5 dakikada otomatik yenileme
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData(true);
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  // Trend hesaplama
  const calculateTrend = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous * 100).toFixed(1);
    return {
      trend: current >= previous ? 'up' : 'down',
      value: `%${Math.abs(change)}`
    };
  };

  // Full page skeleton loading
  if (loading && !dashboardData) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f9fafb' }}>
        <Content style={{ padding: '24px' }}>
          <DashboardSkeleton />
        </Content>
      </Layout>
    );
  }

  const orderStats = dashboardData?.orderStatistics;
  const financialStats = dashboardData?.financialStatistics;
  const basicCounts = dashboardData?.basicCounts;

  const orderTrend = orderStats ? calculateTrend(
    orderStats.thisMonthOrders,
    orderStats.lastMonthOrders
  ) : null;

  const financialTrend = financialStats ? calculateTrend(
    financialStats.thisMonthTurnover,
    financialStats.lastMonthTurnover
  ) : null;

  // Container animation
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <Content style={{ padding: '24px' }} className="dashboard-container">
        {/* Baslik */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col flex="auto">
              <Space direction="vertical" size={0}>
                <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <motion.span
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 5 }}
                  >
                    <DashboardOutlined style={{ color: '#2563eb' }} />
                  </motion.span>
                  Yonetici Paneli
                </Title>
                <Text type="secondary">
                  Son guncelleme: {lastUpdate.toLocaleTimeString('tr-TR')}
                </Text>
              </Space>
            </Col>
            <Col>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  icon={<ReloadOutlined spin={refreshing} />}
                  onClick={handleRefresh}
                  loading={refreshing}
                  type="primary"
                  className="btn-enhanced"
                >
                  Yenile
                </Button>
              </motion.div>
            </Col>
          </Row>
        </motion.div>

        {/* Hata Uyarisi */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Alert
                message="Hata"
                description={error}
                type="error"
                closable
                style={{ marginBottom: 16, borderRadius: 8 }}
                onClose={() => setError(null)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Ana Istatistik Kartlari */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="dashboard-stats-row"
        >
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                title="Toplam Siparisler"
                value={orderStats?.totalOrders || 0}
                icon={<ShoppingCartOutlined />}
                color="#1890ff"
                loading={refreshing}
                trend={orderTrend?.trend}
                trendValue={orderTrend?.value}
                extra={orderStats?.activeOrders > 0 ? `${orderStats.activeOrders} Aktif` : null}
                delay={0}
              />
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <StatCard
                title="Bugunku Ciro"
                value={financialStats?.todayTurnover || 0}
                prefix="₺"
                icon={<DollarOutlined />}
                color="#52c41a"
                loading={refreshing}
                precision={2}
                delay={0.05}
              />
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <StatCard
                title="Toplam Calisanlar"
                value={basicCounts?.totalEmployees || 0}
                icon={<TeamOutlined />}
                color="#faad14"
                loading={refreshing}
                delay={0.1}
              />
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <StatCard
                title="Toplam Sirketler"
                value={basicCounts?.totalCompanies || 0}
                icon={<ShopOutlined />}
                color="#722ed1"
                loading={refreshing}
                delay={0.15}
              />
            </Col>
          </Row>
        </motion.div>

        {/* Ikincil Istatistik Satiri */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <StatCard
                title="Bu Ay Siparisleri"
                value={orderStats?.thisMonthOrders || 0}
                icon={<ShoppingCartOutlined />}
                color="#13c2c2"
                loading={refreshing}
                trend={orderTrend?.trend}
                trendValue={orderTrend?.value}
                delay={0.2}
              />
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <StatCard
                title="Aylik Ciro"
                value={financialStats?.thisMonthTurnover || 0}
                prefix="₺"
                icon={<DollarOutlined />}
                color="#52c41a"
                loading={refreshing}
                precision={2}
                trend={financialTrend?.trend}
                trendValue={financialTrend?.value}
                delay={0.25}
              />
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <StatCard
                title="Ortalama Siparis Degeri"
                value={financialStats?.averageOrderValue || 0}
                prefix="₺"
                color="#eb2f96"
                loading={refreshing}
                precision={2}
                delay={0.3}
              />
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <StatCard
                title="Geciken Siparisler"
                value={orderStats?.overdueOrders || 0}
                icon={<WarningOutlined />}
                color="#ff4d4f"
                loading={refreshing}
                delay={0.35}
                extra={orderStats?.dueSoonOrders > 0 ? `${orderStats.dueSoonOrders} Yaklaşan` : null}
              />
            </Col>
          </Row>
        </motion.div>

        {/* Fatura Istatistikleri */}
        {invoiceStats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
          >
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <StatCard
                  title="Toplam Faturalar"
                  value={invoiceStats.totalInvoices || 0}
                  icon={<FileTextOutlined />}
                  color="#722ed1"
                  loading={refreshing}
                  delay={0.25}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard
                  title="Ödenen Faturalar"
                  value={invoiceStats.paidAmount || 0}
                  prefix="₺"
                  icon={<DollarOutlined />}
                  color="#52c41a"
                  loading={refreshing}
                  precision={2}
                  delay={0.3}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard
                  title="Ödenmemiş Faturalar"
                  value={invoiceStats.unpaidAmount || 0}
                  prefix="₺"
                  icon={<ClockCircleOutlined />}
                  color="#faad14"
                  loading={refreshing}
                  precision={2}
                  delay={0.35}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard
                  title="Vadesi Geçmiş"
                  value={invoiceStats.overdueCount || 0}
                  icon={<WarningOutlined />}
                  color="#ff4d4f"
                  loading={refreshing}
                  delay={0.4}
                />
              </Col>
            </Row>
          </motion.div>
        )}

        {/* Grafik ve Detaylar Satiri */}
        <Row gutter={[16, 16]} className="dashboard-charts-row">
          <Col xs={24} lg={12}>
            <OrderStatusChart
              data={orderStats}
              loading={refreshing}
            />

            {/* Bu Ay En Cok Kullanilan Urunler */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              style={{ marginTop: 16 }}
            >
              <Card
                title="Bu Ay En Cok Kullanilan Urunler"
                className="card-hover-lift"
                style={{ borderRadius: 8 }}
              >
                <Table
                  dataSource={topProducts}
                  rowKey="productName"
                  pagination={false}
                  loading={refreshing}
                  size="small"
                  locale={{ emptyText: 'Bu ay henuz siparis kalemi yok' }}
                  columns={[
                    {
                      title: 'Urun Adi',
                      dataIndex: 'productName',
                      key: 'productName',
                      ellipsis: true,
                    },
                    {
                      title: 'Adet',
                      dataIndex: 'totalQuantity',
                      key: 'totalQuantity',
                      width: 80,
                      align: 'center',
                      render: (val) => (
                        <Text strong style={{ color: '#2563eb' }}>{val}</Text>
                      )
                    },
                    {
                      title: 'Toplam Ciro',
                      dataIndex: 'totalRevenue',
                      key: 'totalRevenue',
                      width: 120,
                      align: 'right',
                      render: (val) => (
                        <Text strong style={{ color: '#059669' }}>
                          ₺{Number(val || 0).toFixed(2)}
                        </Text>
                      )
                    }
                  ]}
                />
              </Card>
            </motion.div>

            {/* Yaklaşan Teslimatlar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.4 }}
              style={{ marginTop: 16 }}
            >
              <Card
                title={
                  <Space>
                    <ClockCircleOutlined style={{ color: '#d97706' }} />
                    Yaklaşan Teslimatlar (7 Gün)
                  </Space>
                }
                className="card-hover-lift"
                style={{ borderRadius: 8 }}
              >
                <Table
                  dataSource={upcomingDeliveries}
                  rowKey="orderId"
                  pagination={false}
                  loading={refreshing}
                  size="small"
                  locale={{ emptyText: 'Yaklaşan teslim tarihi olan sipariş yok' }}
                  columns={[
                    {
                      title: 'Sipariş No',
                      dataIndex: 'orderReference',
                      key: 'orderReference',
                      ellipsis: true,
                    },
                    {
                      title: 'Müşteri',
                      dataIndex: 'companyName',
                      key: 'companyName',
                      ellipsis: true,
                    },
                    {
                      title: 'Teslim Tarihi',
                      dataIndex: 'dueDate',
                      key: 'dueDate',
                      width: 150,
                      render: (val, record) => {
                        const formattedDate = new Date(val).toLocaleDateString('tr-TR');
                        if (record.isOverdue) {
                          return (
                            <Text strong style={{ color: '#dc2626' }}>
                              <WarningOutlined /> {formattedDate}
                            </Text>
                          );
                        }
                        return (
                          <Text style={{ color: '#d97706' }}>
                            <ClockCircleOutlined /> {formattedDate}
                          </Text>
                        );
                      }
                    },
                    {
                      title: 'Kalan Gün',
                      dataIndex: 'daysRemaining',
                      key: 'daysRemaining',
                      width: 100,
                      align: 'center',
                      render: (val, record) => {
                        if (record.isOverdue) {
                          return (
                            <Text strong style={{ color: '#dc2626' }}>
                              {Math.abs(val)} gün gecikti
                            </Text>
                          );
                        }
                        if (val === 0) {
                          return <Text strong style={{ color: '#d97706' }}>Bugün</Text>;
                        }
                        return <Text>{val} gün</Text>;
                      }
                    }
                  ]}
                />
              </Card>
            </motion.div>
          </Col>

          <Col xs={24} lg={12}>
            <FinancialSummary
              data={financialStats}
              loading={refreshing}
            />
          </Col>
        </Row>

        {/* Son Aktiviteler */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <RecentActivity
              orderData={orderStats}
              loading={refreshing}
            />
          </Col>
        </Row>

        {/* Hizli Istatistik Ozeti */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
        >
          <Row style={{ marginTop: 24 }}>
            <Col span={24}>
              <Card
                className="card-hover-lift"
                style={{ borderRadius: 8 }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={8}>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.45 }}
                      style={{ textAlign: 'center' }}
                    >
                      <Text type="secondary">Tamamlanan Siparisler</Text>
                      <div style={{ fontSize: 28, fontWeight: 600, color: '#059669' }}>
                        <CountUp end={orderStats?.completedOrders || 0} duration={1.5} />
                      </div>
                    </motion.div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      style={{ textAlign: 'center' }}
                    >
                      <Text type="secondary">Iptal Edilen Siparisler</Text>
                      <div style={{ fontSize: 28, fontWeight: 600, color: '#dc2626' }}>
                        <CountUp end={orderStats?.cancelledOrders || 0} duration={1.5} />
                      </div>
                    </motion.div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.55 }}
                      style={{ textAlign: 'center' }}
                    >
                      <Text type="secondary">Basarisiz Siparisler</Text>
                      <div style={{ fontSize: 28, fontWeight: 600, color: '#94a3b8' }}>
                        <CountUp end={orderStats?.failedOrders || 0} duration={1.5} />
                      </div>
                    </motion.div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </motion.div>
      </Content>
    </Layout>
  );
};

export default AdminDashboardPage;
